import { useState, type ReactNode } from "react";
import type { PreguntaCopiloto, RecomendacionParaProducto } from "@huayruro/shared";

// ============================================================
// Copiloto del mostrador, etapa 1 (Kevin, 16-oct-2026): SIEMPRE visible, desde que empieza la atención.
//   · Preguntas: recordatorio. Marcar una es solo para quien atiende («ya la hice»); no se guarda nada
//     y se limpia al terminar la venta. Guardar respuestas es la etapa 2 (con consentimiento).
//   · Recomendaciones de uso de lo que hay en el carrito, por principio activo.
// Dos formas: «columna» (escritorio ancho, arriba de Seguimiento) y «franja» (pantallas más angostas,
// sobre la búsqueda). El contenido es el mismo; la franja es más compacta.
// ============================================================

type Props = {
  variante: "columna" | "franja";
  preguntas: PreguntaCopiloto[];
  recomendaciones: RecomendacionParaProducto[];
  /** ¿Hay algo en el carrito? Cambia el texto de «sin recomendaciones». */
  conProductos: boolean;
  marcadas: Set<string>;
  onMarcar: (id: string) => void;
  /** Dueño o Encargado: ven dónde se configura. */
  puedeConfigurar: boolean;
  /** Clientes por cara (tramo 10): la tarjeta «¿Es Nombre?» va arriba de todo, en las dos formas. */
  arriba?: ReactNode;
};

export function PanelCopiloto({ variante, preguntas, recomendaciones, conProductos, marcadas, onMarcar, puedeConfigurar, arriba }: Props) {
  const [abierto, setAbierto] = useState(false);
  const sinNada = preguntas.length === 0 && recomendaciones.length === 0;
  const pendientes = preguntas.filter((p) => !marcadas.has(p.id)).length;

  const chips = (
    <ul className="flex flex-wrap gap-1.5" aria-label="Preguntas para el cliente">
      {preguntas.map((p) => {
        const hecha = marcadas.has(p.id);
        return (
          <li key={p.id}>
            <button
              type="button"
              onClick={() => onMarcar(p.id)}
              aria-pressed={hecha}
              className={`inline-flex min-h-[36px] items-center gap-1.5 rounded-full border px-3 text-left text-[12.5px] leading-snug ${
                hecha ? "border-ok/30 bg-ok-soft text-ok line-through decoration-ok/60" : "border-info/30 bg-info-soft text-info-ink"
              }`}
            >
              <span aria-hidden="true">{hecha ? "✓" : "?"}</span>
              {p.texto}
            </button>
          </li>
        );
      })}
    </ul>
  );

  const listaRecs = (tope: number | null) => {
    const visibles = tope === null ? recomendaciones : recomendaciones.slice(0, tope);
    return (
      <ul className="flex flex-col gap-2">
        {visibles.map((r) => (
          <li key={r.producto_id} className="rounded-[10px] border border-tip-border bg-ok-soft/60 px-3 py-2">
            <p className="text-[12.5px] font-semibold text-ink">{r.nombre}</p>
            {r.recomendaciones.map((x) => (
              <p key={x.id} className="text-[12.5px] leading-snug text-ink-2">
                {x.texto}
              </p>
            ))}
          </li>
        ))}
      </ul>
    );
  };

  const ayuda = sinNada && (
    <p className="text-[12.5px] leading-snug text-ink-3">
      {puedeConfigurar ? "Todavía no hay preguntas ni recomendaciones. Se escriben en Panel › Copiloto." : "Todavía no hay preguntas ni recomendaciones configuradas."}
    </p>
  );

  if (variante === "columna") {
    return (
      <section className="flex flex-col gap-3" data-testid="copiloto" aria-label="Copiloto">
        {arriba}
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="text-[14px] font-semibold text-ink">🧭 Copiloto</h2>
          {preguntas.length > 0 && <span className="text-[11.5px] text-ink-3 tabular-nums">{pendientes} por preguntar</span>}
        </div>
        {ayuda}
        {preguntas.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-3">Antes de vender, pregunta</p>
            {chips}
          </div>
        )}
        {!sinNada && (
          <div className="flex flex-col gap-1.5">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-3">Recomendaciones de uso</p>
            {recomendaciones.length > 0 ? (
              listaRecs(null)
            ) : (
              <p className="text-[12.5px] text-ink-3">
                {conProductos ? "Lo que lleva no tiene recomendaciones escritas." : "Agrega un producto: aquí salen sus recomendaciones."}
              </p>
            )}
          </div>
        )}
      </section>
    );
  }

  // Franja: preguntas siempre a la vista; recomendaciones, la primera y «ver todas».
  return (
    <section className="mb-3 flex flex-col gap-2 rounded-[12px] border border-line bg-card px-3 py-2.5" data-testid="copiloto" aria-label="Copiloto">
      {arriba}
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-[13px] font-semibold text-ink">🧭 Copiloto</h2>
        {preguntas.length > 0 && <span className="text-[11.5px] text-ink-3 tabular-nums">{pendientes} por preguntar</span>}
      </div>
      {ayuda}
      {preguntas.length > 0 && chips}
      {recomendaciones.length > 0 && (
        <>
          {listaRecs(abierto ? null : 1)}
          {recomendaciones.length > 1 && (
            <button type="button" onClick={() => setAbierto((v) => !v)} className="self-start text-[12px] text-link underline">
              {abierto ? "Ver menos" : `Ver las ${recomendaciones.length} recomendaciones`}
            </button>
          )}
        </>
      )}
    </section>
  );
}
